<script setup lang="ts">
import { onBeforeMount, onBeforeUpdate, Ref, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useStoreProfiles } from '../store/useStoreProfiles'
import BaseTypeAhead from './BaseTypeAhead.vue'
import { useI18n } from 'vue-i18n'

//translations
const { t } = useI18n({ useScope: 'global' })
const formOption = (key: string) => t(`form.options.${key}`)
const errorMessage = (key: string) => t(`form.messages.${key}`)

//component setup
const props = defineProps<{
  label: string
  modelValue: any
  rules: any[]
}>()
const emit = defineEmits<{ (e: 'update:modelValue', data: any): void }>()

const locationOptions = ref([
  { name: 'home', type: 'home' },
  { name: 'warehouse', type: 'warehouse' },
  { name: 'other', type: 'other' },
])
const { getStores } = storeToRefs(useStoreProfiles())
const storeList = ref(
  getStores.value.data.map((store) => {
    return {
      type: 'store',
      store_id: store.store_id,
      name: store.store_name,
      number: store.store_number,
      address: {
        street: store.street,
        city: store.city,
        state: store.state,
        zip: store.zip,
      },
    }
  })
)
const allLocationOptions = ref([...locationOptions.value, ...storeList.value]) as Ref<
  {
    name: string
    type: 'home' | 'warehouse' | 'store' | 'other'
    number?: number
    address?: { street: string; city: string; state: string; zip: string }
  }[]
>

const update = (e: any) => {
  currentValue.value = e
  emit('update:modelValue', e)
}
const options = ref([...allLocationOptions.value])

const currentValue = ref()
onBeforeMount(() => {
  currentValue.value = props.modelValue
})
onBeforeUpdate(() => {
  currentValue.value = props.modelValue
})
const BaseTypeAheadRef = ref()
defineExpose({
  validate: () =>
    BaseTypeAheadRef.value ? BaseTypeAheadRef.value.validate() : console.log('no ref exists yet'),
})
</script>

<template>
  <BaseTypeAhead
    ref="BaseTypeAheadRef"
    :label="label"
    v-model:selected="currentValue"
    :dropdown-options="options"
    @update:selected="update"
    :search-keys="['name', 'number']"
    :rules="rules"
  >
    <template #selected-item="{ opt }">
      <template v-if="opt.type !== 'store'">
        {{ formOption(opt.name) }}
      </template>
      <template v-else>
        {{ `${opt.name} #${opt.number}` }}
      </template>
    </template>
    <template #option="optionProps">
      <q-item
        clickable
        v-close-popup
        @click=";(currentValue = optionProps.opt), $emit('update:modelValue', currentValue)"
      >
        <template v-if="optionProps.opt.type !== 'store'">
          {{ formOption(optionProps.opt.name) }}
        </template>
        <template v-else>
          {{ `${optionProps.opt.name} #${optionProps.opt.number}` }}
        </template>
      </q-item>
    </template>
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey"> {{ errorMessage('noResults') }} </q-item-section>
      </q-item>
    </template>
  </BaseTypeAhead>
</template>
